import React from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { apiClient } from "@/lib/api";
import { useQuery } from "@tanstack/react-query";
import { LayoutDashboard, KanbanSquare, Users, QrCode, Dumbbell, Activity, Gift, Award, Settings, LogOut, ChevronDown, ChevronsRight, ClipboardCheck, Shield, Megaphone, Coins, PackageCheck, Briefcase, IndianRupee, CalendarClock, Stethoscope, UserCog, Wallet, Globe, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { ROLE_LABELS } from "@/lib/constants";

const NAV = {
  SUPER_ADMIN: [
    { to: "/dashboard", icon: LayoutDashboard, label: "Command Center" },
    { to: "/sales", icon: KanbanSquare, label: "Sales CRM" },
    { to: "/members", icon: Users, label: "Members" },
    { to: "/attendance", icon: QrCode, label: "Attendance" },
    { to: "/coach", icon: Dumbbell, label: "Coach Hub" },
    { to: "/schedule", icon: CalendarClock, label: "Schedule" },
    { to: "/retention", icon: Activity, label: "Retention" },
    { to: "/referrals", icon: Gift, label: "Referrals" },
    { to: "/operations", icon: ClipboardCheck, label: "Operations" },
    { to: "/approvals", icon: Shield, label: "Approvals" },
    { to: "/marketing", icon: Megaphone, label: "Marketing" },
    { to: "/website-embed", icon: Globe, label: "Website Form" },
    { to: "/bulk-import", icon: Upload, label: "Bulk Import" },
    { to: "/rewards", icon: Coins, label: "Rewards" },
    { to: "/redemptions", icon: PackageCheck, label: "Redemptions" },
    { to: "/jobs", icon: Briefcase, label: "Jobs" },
    { to: "/staff", icon: UserCog, label: "Staff" },
    { to: "/assessments", icon: Stethoscope, label: "Assessments" },
    { to: "/reports", icon: Award, label: "Reports" },
    { to: "/finance", icon: IndianRupee, label: "Finance" },
    { to: "/expenses", icon: Wallet, label: "Expenses" },
  ],
  ADMIN: [
    { to: "/dashboard", icon: LayoutDashboard, label: "Dashboard" },
    { to: "/sales", icon: KanbanSquare, label: "Sales CRM" },
    { to: "/members", icon: Users, label: "Members" },
    { to: "/attendance", icon: QrCode, label: "Attendance" },
    { to: "/schedule", icon: CalendarClock, label: "Schedule" },
    { to: "/retention", icon: Activity, label: "Retention" },
    { to: "/referrals", icon: Gift, label: "Referrals" },
    { to: "/operations", icon: ClipboardCheck, label: "Operations" },
    { to: "/approvals", icon: Shield, label: "Approvals" },
    { to: "/marketing", icon: Megaphone, label: "Marketing" },
    { to: "/website-embed", icon: Globe, label: "Website Form" },
    { to: "/redemptions", icon: PackageCheck, label: "Redemptions" },
    { to: "/jobs", icon: Briefcase, label: "Jobs" },
    { to: "/assessments", icon: Stethoscope, label: "Assessments" },
    { to: "/finance", icon: IndianRupee, label: "Finance" },
    { to: "/expenses", icon: Wallet, label: "Expenses" },
  ],
  COACH: [
    { to: "/dashboard", icon: LayoutDashboard, label: "Dashboard" },
    { to: "/coach", icon: Dumbbell, label: "Coach Hub" },
    { to: "/members", icon: Users, label: "My Members" },
    { to: "/attendance", icon: QrCode, label: "Attendance" },
    { to: "/assessments", icon: Stethoscope, label: "Assessments" },
    { to: "/operations", icon: ClipboardCheck, label: "Operations" },
    { to: "/jobs", icon: Briefcase, label: "Jobs" },
  ],
  CUSTOMER: [
    { to: "/me", icon: LayoutDashboard, label: "My Hub" },
    { to: "/jobs", icon: Briefcase, label: "Jobs" },
  ],
};

export default function AppShell({ children }) {
  const { user, logout, activeBranchId, setBranch } = useAuth();
  const navigate = useNavigate();
  const role = user?.role || "CUSTOMER";
  const items = NAV[role] || [];

  const { data: branches = [] } = useQuery({
    queryKey: ["branches"],
    queryFn: async () => (await apiClient.get("/branches")).data,
    enabled: !!user,
  });

  const visibleBranches = (user?.branch_ids && user.role !== "SUPER_ADMIN")
    ? branches.filter((b) => user.branch_ids.includes(b.id))
    : branches;

  const activeBranch = branches.find((b) => b.id === activeBranchId);

  React.useEffect(() => {
    if (!activeBranchId && visibleBranches.length > 0 && user?.role !== "SUPER_ADMIN") {
      setBranch(visibleBranches[0].id);
    }
  }, [visibleBranches, activeBranchId, user, setBranch]);

  return (
    <div className="min-h-screen flex bg-background text-foreground" data-testid="app-shell">
      {/* Sidebar */}
      <aside className="w-[240px] shrink-0 border-r border-zinc-900 bg-[#070707] flex flex-col" data-testid="sidebar">
        <Link to="/dashboard" className="px-5 py-5 border-b border-zinc-900 flex items-center gap-2">
          <div className="w-9 h-9 bg-volt grid place-items-center rounded-sm">
            <span className="font-display text-2xl font-black text-black leading-none">N</span>
          </div>
          <div className="leading-none">
            <div className="font-display text-xl font-black tracking-tight uppercase">NammaXFit</div>
            <div className="text-[10px] tracking-[0.2em] uppercase text-zinc-500">Command OS</div>
          </div>
        </Link>

        <nav className="flex-1 py-4 px-3 space-y-1">
          {items.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              data-testid={`nav-${it.to.replace("/", "")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-sm transition-colors duration-150 ` +
                (isActive
                  ? "bg-zinc-900 text-volt border-l-2 border-volt"
                  : "text-zinc-400 hover:text-white hover:bg-zinc-900/60 border-l-2 border-transparent")
              }
            >
              <it.icon size={16} strokeWidth={2} />
              <span className="text-sm font-medium tracking-wide">{it.label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="px-3 py-4 border-t border-zinc-900">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid="user-menu-trigger"
                className="w-full flex items-center gap-3 px-2 py-2 rounded-sm hover:bg-zinc-900 text-left"
              >
                <div className="w-9 h-9 rounded-sm bg-zinc-800 grid place-items-center text-volt font-display text-base font-bold">
                  {(user?.full_name || "U").slice(0, 1).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold truncate">{user?.full_name}</div>
                  <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">{ROLE_LABELS[role]}</div>
                </div>
                <ChevronDown size={14} className="text-zinc-500" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" side="top" className="bg-[#0b0b0b] border-zinc-800">
              <DropdownMenuLabel className="text-xs text-zinc-500 uppercase tracking-wider">{user?.email}</DropdownMenuLabel>
              <DropdownMenuSeparator className="bg-zinc-800" />
              <DropdownMenuItem onClick={() => { logout(); navigate("/login"); }} data-testid="logout-button" className="text-red-400 cursor-pointer">
                <LogOut size={14} className="mr-2" />Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        {/* Top bar */}
        <header className="h-14 border-b border-zinc-900 bg-black/60 backdrop-blur-xl flex items-center px-6 gap-4 sticky top-0 z-30">
          <div className="flex items-center gap-2 text-zinc-500 text-xs uppercase tracking-[0.18em]">
            <ChevronsRight size={14} />
            <span>{activeBranch ? activeBranch.name : "All Branches"}</span>
          </div>

          <div className="ml-auto flex items-center gap-2">
            {visibleBranches.length > 0 && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" data-testid="branch-switcher" className="bg-zinc-900 border-zinc-800 text-white hover:bg-zinc-800">
                    Branch: <span className="ml-1 text-volt font-semibold">{activeBranch ? activeBranch.name : "All"}</span>
                    <ChevronDown size={14} className="ml-2" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-[#0b0b0b] border-zinc-800 min-w-[220px]">
                  {user?.role === "SUPER_ADMIN" && (
                    <DropdownMenuItem onClick={() => setBranch(null)} data-testid="branch-option-all">All Branches</DropdownMenuItem>
                  )}
                  {visibleBranches.map((b) => (
                    <DropdownMenuItem key={b.id} onClick={() => setBranch(b.id)} data-testid={`branch-option-${b.id}`}>
                      <div className="flex flex-col">
                        <span>{b.name}</span>
                        <span className="text-[10px] text-zinc-500 uppercase tracking-wider">{b.city} • Cap {b.capacity}</span>
                      </div>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </header>

        <main className="flex-1 overflow-auto scrollbar-thin">
          {children}
        </main>
      </div>
    </div>
  );
}
